import { computed } from '@nuxtjs/composition-api'
import { readFromBinaryFile } from 'exif-js'

import {
  base64ToFile,
  extractOrientationValue,
  resizeImage
} from '~/compositions/files/utils'
import { ResizedPhoto } from '~/models/photos/types'
import { useI18n } from '~/compositions/i18n'

export function useFile() {
  const { t } = useI18n()
  const imageOrientationSupported = computed(() => {
    return document.createElement('img').style.imageOrientation !== undefined
  })
  function fileToBase64(file: File): Promise<string> {
    return new Promise(resolve => {
      const fileReader = new FileReader()
      fileReader.readAsDataURL(file)
      fileReader.onload = (e: any) => {
        resolve(e.target?.result)
      }
    })
  }
  function readImageOrientation(file: File): Promise<any> {
    return new Promise(resolve => {
      if (imageOrientationSupported) {
        resolve({ orientation: -1 })
      }

      const orientationReader = new FileReader()
      orientationReader.onload = (e: any) => {
        try {
          const make = readFromBinaryFile(e.target?.result)?.Make
          resolve({
            orientation: extractOrientationValue(e.target?.result),
            make
          })
        } catch (e) {
          resolve({ orientation: -1 })
        }
      }
      orientationReader.readAsArrayBuffer(file)
    })
  }

  async function resizeAndReadImage(
    file: File,
    maxResizeWidth: number = 2100,
    maxResizeHeight: number = 1708,
    exactOnly: boolean = false, // throws error if image is not === the above values,
    minWidth: number = 0,
    minHeight: number = 0
  ): Promise<ResizedPhoto> {
    const imageSource = await fileToBase64(file)
    const { orientation, make } = await readImageOrientation(file)
    const image = new Image()
    return new Promise((resolve, reject) => {
      image.onload = (event: any) => {
        const eventWidth = event.target?.width
        const eventHeight = event.target?.height
        let width = event.target?.width
        let height = event.target?.height
        if (orientation > 4 && orientation < 9) {
          // intentionally flipping here
          width = eventHeight
          height = eventWidth
        }

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('canvas not supported'))
          return
        }

        // throw error if image is not exactly at max values
        if (
          exactOnly &&
          width !== maxResizeWidth &&
          height !== maxResizeHeight
        ) {
          const error = t('image_not_exact_dimensions', {
            size: `${maxResizeWidth}x${maxResizeHeight}`
          }).toString()
          alert(error)
          throw new TypeError(error)
        }

        // Don't resize if it's small enough
        if (
          width <= maxResizeWidth &&
          height <= maxResizeHeight &&
          width >= minWidth &&
          height >= minHeight &&
          orientation < 0
        ) {
          canvas.width = image.width
          canvas.height = image.height
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
          resolve({
            photo: base64ToFile(canvas.toDataURL('image/jpeg'), file),
            thumb: canvas.toDataURL('image/jpeg'),
            width,
            height
          })
          return
        }

        // Attempt to resize
        try {
          const {
            file: resizedFile,
            thumb,
            width: resizeWidth,
            height: resizeHeight
          } = resizeImage({
            canvas,
            initWidth: width,
            initHeight: height,
            maxResizeWidth,
            maxResizeHeight,
            minWidth,
            minHeight,
            orientation,
            image,
            eventWidth,
            eventHeight,
            file,
            make
          })
          resolve({
            photo: resizedFile,
            thumb,
            width: resizeWidth,
            height: resizeHeight
          })
          return
        } finally {
          // If it fails fallback to drawing the image as is
          ctx.drawImage(image, 0, 0, image.width, image.height)
          resolve({
            photo: base64ToFile(canvas.toDataURL('image/jpeg'), file),
            thumb: canvas.toDataURL('image/jpeg'),
            width,
            height
          })
        }
      }
      image.src = imageSource
    })
  }
  return {
    fileToBase64,
    resizeAndReadImage
  }
}
